/*
 * File: Button.styles.js
 * Project: seesaw-marketplace-app
 *
 * Created by Brendan Michaelsen on February 7, 2022 at 1:53 PM
 * Copyright © 2022 Seesaw Technologies, LLC. All rights reserved.
 *
 * Last Modified: July 24, 2022 at 10:55 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import styled, { css } from 'styled-components';

// Styles
import { ColorValuesCreator } from '../../styles/colors';


/**
 * Constants
 */

const SIZES = {
	small: css`
		padding: 0.3125rem 0.625rem;
	`,
	medium: css`
		padding: 0.5625rem 1.5625rem;
	`,
	large: css`
		padding: 0.8625rem 1.8625rem;
	`,
};

const VARIATIONS = {
	default: {
		solid: css`
			background-color: ${({ theme }) => theme.brandPrimaryBase};
			border-color: ${({ theme }) => theme.brandPrimaryBase};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.brandPrimaryBase};
			color: ${({ theme }) => theme.brandPrimaryBase};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.brandPrimaryBase};
		`,
	},
	light: {
		solid: css`
			background-color: ${({ theme }) => theme.lightButton};
			border-color: ${({ theme }) => theme.lightButton};
			color: ${({ theme }) => theme.brandPrimaryBase};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.brandPrimaryBase};
			color: ${({ theme }) => theme.brandPrimaryBase};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.brandPrimaryBase};
		`,
	},
	white: {
		solid: css`
			background-color: ${({ merchant }) => ColorValuesCreator(merchant).white};
			border-color: ${({ merchant }) => ColorValuesCreator(merchant).white};
			color: ${({ merchant }) => ColorValuesCreator(merchant).dark};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ merchant }) => ColorValuesCreator(merchant).white};
			color: ${({ merchant }) => ColorValuesCreator(merchant).white};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ merchant }) => ColorValuesCreator(merchant).white};
		`,
	},
	dark: {
		solid: css`
			background-color: ${({ theme }) => theme.darkButton};
			border-color: ${({ theme }) => theme.darkButton};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.darkButton};
			color: ${({ theme }) => theme.darkButton};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.darkButton};
		`,
	},
	secondary: {
		solid: css`
			background-color: ${({ theme }) => theme.secondaryButton};
			border-color: ${({ theme }) => theme.secondaryButton};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.secondaryButton};
			color: ${({ theme }) => theme.secondaryButton};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.secondaryButton};
		`,
	},
	warning: {
		solid: css`
			background-color: ${({ theme }) => theme.statusDangerBase};
			border-color: ${({ theme }) => theme.statusDangerBase};
			color: ${({ theme }) => theme.primaryBackground};
		`,
		outline: css`
			background-color: transparent;
			border-color: ${({ theme }) => theme.statusDangerBase};
			color: ${({ theme }) => theme.statusDangerBase};
		`,
		text: css`
			background-color: transparent;
			border-color: transparent;
			color: ${({ theme }) => theme.statusDangerBase};
		`,
	},
};


/**
 * Styles
 */

export const Button = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	appearance: none;
	border-radius: 6px;
	letter-spacing: 0.04rem;
	cursor: pointer;
	border-width: 1px;
	border-style: solid;
	font-weight: 700;
	font-size: 0.9rem;

	* {
		letter-spacing: 0.04rem;
	}

	& > * + * {
		margin-left: 0.625rem;
	}
	.noMargin {
		margin-left: 0px;
	}

	${({ size, variant, variation }) => css`
		${SIZES[size]}
		${VARIATIONS[variation][variant]}
	`}

	&:hover {
		opacity: 0.8;
	}

	&:disabled {
		opacity: 0.8;
		cursor: default;
	}
`;
